/**
 * @file ShopDomainsTab.tsx
 * @description Вкладка «Домены» карточки магазина (phase19.28 этап А;
 * uiux-platform-shops §5, §7; PL10–PL12, SEO4, В11).
 *
 * Список адресов: главный → переадресации → поддомен платформы; статус —
 * одна пилюля (первая неудача DNS → прокси → сертификат), DNS / прокси /
 * HTTPS значками с датой проверки. «⋯»: Проверить, Инструкция, Сделать
 * главным (только работающий), Отключить, Удалить (только неосновной свой
 * домен). Ниже — заявки владельца на свой домен за 30 дней, «Как подключить
 * домен» и «Неизвестные адреса за 7 дней»; «Добавить» подставляет адрес в форму.
 */

import { Globe, Plus } from "lucide-react";
import { useState } from "react";

import { useShopCardContext } from "../../../hooks/platform/useShopCard";
import { useShopDomainActions } from "../../../hooks/platform/useShopDomainActions";
import { usePlatformEdgeConfig } from "../../../hooks/platform/usePlatformEdgeConfig";
import { usePlatformResource } from "../../../hooks/platform/usePlatformResource";
import { platformShops, type ShopDomain } from "../../../lib/api-platform-shops";
import { sortDomains } from "../../../lib/platform/shop-domain-status";
import { AddDomainDialog } from "../../../components/platform/shops/AddDomainDialog";
import { domainColumns, domainRowActions } from "../../../components/platform/shops/domain-columns";
import { DomainGuideDialog } from "../../../components/platform/shops/DomainGuideDialog";
import { DomainRequestsCard } from "../../../components/platform/shops/DomainRequestsCard";
import { UnknownDomainsList } from "../../../components/platform/shops/UnknownDomainsList";
import { Button } from "../../../components/ui/button";
import { ConfirmDialog } from "../../../components/ui/ConfirmDialog";
import { NoticeStrip } from "../../../components/ui/NoticeStrip";
import { ResponsiveList } from "../../../components/ui/ResponsiveList";
import { SectionCard } from "../../../components/ui/SectionCard";

export function ShopDomainsTab() {
  const { shop, reload } = useShopCardContext();
  const actions = useShopDomainActions(shop.id, reload);
  const edge = usePlatformEdgeConfig();
  const [adding, setAdding] = useState<{ host: string } | null>(null);
  const [guideId, setGuideId] = useState<string | null>(null);
  const unknown = usePlatformResource(
    "domains/unknown",
    () => platformShops.unknownDomains(7),
    "Не удалось загрузить журнал неизвестных адресов",
  );
  const domains = sortDomains(shop.domains);
  const guideDomain = domains.find((d) => d.id === guideId) ?? null;
  // «Открыть инструкцию» — по первому своему домену, иначе по главному адресу.
  const guideTarget = domains.find((d) => d.kind === "CUSTOM") ?? domains.find((d) => d.isPrimary);
  const handlers = {
    recheck: (d: ShopDomain) => void actions.recheck(d),
    guide: (d: ShopDomain) => setGuideId(d.id),
    makePrimary: (d: ShopDomain) => void actions.makePrimary(d),
    toggle: (d: ShopDomain) => void actions.toggle(d),
    remove: (d: ShopDomain) => void actions.remove(d),
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      <Button variant="soft" className="self-start" onClick={() => setAdding({ host: "" })}>
        <Plus className="size-4" aria-hidden />
        Добавить домен
      </Button>

      {actions.checkError ? <NoticeStrip tone="error">{actions.checkError.text}</NoticeStrip> : null}
      <span className="sr-only" aria-live="polite">
        {actions.announce}
      </span>

      <ResponsiveList
        aria-label="Адреса магазина"
        rows={domains}
        columns={domainColumns(shop.primaryHost, actions.checkingId, edge.publicIps)}
        rowKey={(d) => d.id}
        rowActions={(d) => domainRowActions(d, handlers, actions.checkingId === d.id)}
        rowActionsTitle={(d) => `Адрес ${d.hostUnicode || d.host}`}
        compactRowSize="tall"
        compactWrap
        empty={{ icon: Globe, title: "Адресов нет", description: "Поддомен платформы появляется при создании магазина." }}
      />

      <DomainRequestsCard
        requests={shop.domainRequests ?? []}
        boundHosts={new Set(domains.map((d) => d.host))}
        onAdd={(host) => setAdding({ host })}
      />

      <SectionCard title="Как подключить домен">
        <ol className="type-body flex list-decimal flex-col gap-1 pl-5">
          <li>У регистратора домена — A-запись на IP сервера платформы (и www).</li>
          <li>Владелец платформы заводит хост в NPM по памятке — после того как DNS заработал.</li>
          <li>Здесь — «Проверить»: DNS, прокси и HTTPS должны стать ✓.</li>
        </ol>
        {/* Путь памятки в репозитории не показываем (QA D-B8): та же инструкция в окне. */}
        <Button variant="outline" className="mt-2 self-start" onClick={() => setGuideId((guideTarget ?? domains[0])?.id ?? null)} disabled={domains.length === 0}>
          Открыть инструкцию
        </Button>
        <p className="type-caption mt-1 text-on-surface-variant">У каждого адреса — «⋯ → Инструкция».</p>
      </SectionCard>

      <SectionCard title="Неизвестные адреса за 7 дней">
          <UnknownDomainsList
            items={unknown.data ?? []}
            loading={unknown.loading}
            error={unknown.error}
            onRetry={unknown.reload}
            bindLabel="Добавить к магазину"
            onBind={(item) => setAdding({ host: item.host })}
          />
        </SectionCard>

      {adding ? (
        <AddDomainDialog
          shopId={shop.id}
          initialHost={adding.host}
          onClose={() => setAdding(null)}
          onAdded={(domain) => {
            setAdding(null);
            setGuideId(domain.id);
            reload();
            unknown.reload();
          }}
        />
      ) : null}
      <DomainGuideDialog
        domain={guideDomain}
        checking={!!guideDomain && actions.checkingId === guideDomain.id}
        checkError={guideDomain && actions.checkError?.domainId === guideDomain.id ? actions.checkError.text : null}
        onRecheck={handlers.recheck}
        onClose={() => setGuideId(null)}
      />
      <ConfirmDialog request={actions.confirmApi.request} onSettle={actions.confirmApi.settle} onDismiss={actions.confirmApi.dismiss} />
    </div>
  );
}
